import * as React from "react";
import { createPortal } from "react-dom";
import type { SortingState, Table } from "@tanstack/react-table";
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ListChecksIcon,
  MoreHorizontalIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";

import { useIsMobile } from "../../../lib/use-mobile";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { SegmentedControl } from "../../ui/segmented-control";
import { MobileSearchField } from "../../ui/mobile-search-field";
import { MobileScreenHeader } from "../../ui/mobile-screen-header";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "../../ui/drawer";
import { useDataTableMobileLabels, useDataTableMobileList } from "./data-table-mobile-list";
import type {
  TableFilterConfig,
  TableFilterValues,
} from "./data-table-filter-types";
import { DataTableFilters } from "./data-table-filters";
import {
  type DataTableAction,
  type DataTableToolbarRenderContext,
  DataTableToolbarActions,
} from "./data-table-toolbar";
import { DataTableViewOptions } from "./data-table-view-options";

export type DataTableToolbarViewItem<TValue extends string> = {
  value: TValue;
  label: React.ReactNode;
  disabled?: boolean;
  /** Shown beside the label on the phone's view chips (e.g. how many visitors). */
  count?: number;
};

export type DataTableToolbarViewsConfig<TValue extends string> = {
  value: TValue;
  onValueChange: (value: TValue) => void;
  items: DataTableToolbarViewItem<TValue>[];
  render?: (
    context: DataTableToolbarRenderContext & {
      value: TValue;
      items: DataTableToolbarViewItem<TValue>[];
    },
  ) => React.ReactNode;
};

export type DataTableToolbarSearchConfig = {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  label: string;
  debounceMs?: number;
  render?: (
    context: DataTableToolbarRenderContext & {
      value: string;
      expanded: boolean;
      setExpanded: (expanded: boolean) => void;
    },
  ) => React.ReactNode;
};

export type DataTableToolbarSortingConfig = {
  items: Array<{ id: string; label: string }>;
  label: string;
  ascendingLabel: string;
  descendingLabel: string;
  render?: (
    context: DataTableToolbarRenderContext & {
      sorting: SortingState;
      onSortingChange: (sorting: SortingState) => void;
    },
  ) => React.ReactNode;
};

export type DataTableToolbarFiltersConfig = {
  config: TableFilterConfig;
  appliedValues: TableFilterValues;
  onApply: (values: TableFilterValues) => void;
  onReset: () => void;
};

export type DataTableToolbarDisplayConfig = {
  label: string;
  activeColumnsLabel: string;
  availableColumnsLabel: string;
  emptyColumnsLabel: string;
  resetLabel: string;
  render?: (context: DataTableToolbarRenderContext) => React.ReactNode;
};

export type DataTableToolbarBulkActionsConfig = {
  selectedLabel: (count: number) => string;
  actions: DataTableAction[];
};

export type DataTableToolbarConfig<TData, TValue extends string = string> = {
  /**
   * Phone only: the page's title row, drawn by the toolbar so the page actions (primary button and ⋯) sit beside
   * it. A screen that passes this hides its own heading on phones.
   */
  mobileHeader?: {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /**
     * The round back button before the title. Omitted: the screen's default back (every stack screen gets one
     * from the app shell, see MobileScreenHeaderProvider). `null`: none.
     */
    back?: { label: string; onBack: () => void } | null;
  };
  /**
   * Phone only: content drawn between the search row and the view chips, for a list that opens on a short
   * summary (e.g. "this week") before its rows.
   */
  mobileIntro?: React.ReactNode;
  /** Phone only: tabs drawn right under the title row (a screen whose list is one of several sibling views). */
  mobileTabs?: React.ReactNode;
  views?: DataTableToolbarViewsConfig<TValue>;
  search?: DataTableToolbarSearchConfig;
  sorting?: DataTableToolbarSortingConfig;
  filters?: DataTableToolbarFiltersConfig;
  display?: DataTableToolbarDisplayConfig;
  primaryAction?: DataTableAction | DataTableAction[];
  secondaryActions?: DataTableAction[];
  bulkActions?: DataTableToolbarBulkActionsConfig;
  /**
   * Desktop only: the list row reads views (full labels, never truncated), then an always-open search field
   * and the filter button on the left; sorting, display and the actions stay on the right. Phone unchanged.
   */
  inlineSearch?: boolean;
  className?: string;
};

/**
 * A toolbar icon's label, WITHOUT a Radix trigger.
 *
 * This used to wrap its child in `<TooltipTrigger asChild>`. That child is a `Button`, which renders a Slot of
 * its own, so two Radix slots composed refs onto one `<button>`: `composeRefs(tooltipRef, composeRefs(...))`.
 * Slot rebuilds that composition unmemoised every render and `TooltipTrigger` stores its node in state, so
 * attaching the ref dispatched state during the layout-effect phase, which re-attached it, which dispatched
 * again — "Maximum update depth exceeded", thrown from inside `composeRefs` with no component of ours named.
 *
 * `title` gives the same hover label with no trigger and no ref composition, matching the overflow menu, the
 * filters sheet, the view-options menu and the toolbar actions in this folder.
 */
function toolbarIconTitle(label: React.ReactNode): string | undefined {
  return typeof label === "string" && label.length > 0 ? label : undefined;
}

function ToolbarSearch({
  config,
  persistentExpanded = false,
  widthClassName = "w-72",
}: {
  config: DataTableToolbarSearchConfig;
  persistentExpanded?: boolean;
  /** Desktop width of the open field. */
  widthClassName?: string;
}) {
  const isMobile = useIsMobile();
  const mobileLabels = useDataTableMobileLabels();
  const [expanded, setExpanded] = React.useState(Boolean(config.value) || persistentExpanded);
  const [draft, setDraft] = React.useState(config.value);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setDraft(config.value);
    if (config.value) setExpanded(true);
    if (persistentExpanded) setExpanded(true);
  }, [config.value, persistentExpanded]);

  React.useEffect(() => {
    if (!expanded) return;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [expanded]);

  React.useEffect(() => {
    const delay = config.debounceMs ?? 0;
    if (!expanded) return;
    if (delay <= 0 || draft === config.value) return;
    const timer = window.setTimeout(() => config.onValueChange(draft), delay);
    return () => window.clearTimeout(timer);
  }, [config, draft, expanded]);

  React.useEffect(() => {
    if (persistentExpanded || !expanded || draft) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [draft, expanded]);

  const closeSearch = React.useCallback(() => {
    if (draft || config.value) {
      setDraft("");
      config.onValueChange("");
    }
    if (!persistentExpanded) setExpanded(false);
  }, [config, draft, persistentExpanded]);

  const context: DataTableToolbarRenderContext & {
    value: string;
    expanded: boolean;
    setExpanded: (next: boolean) => void;
  } = {
    isMobile,
    inOverflow: false,
    close: closeSearch,
    value: draft,
    expanded,
    setExpanded,
  };
  if (config.render) return <>{config.render(context)}</>;

  // A phone uses the shared search field (the same one as the members overview).
  if (isMobile) {
    return (
      <MobileSearchField
        value={draft}
        onValueChange={(next) => {
          setDraft(next);
          // Clearing applies at once; typing waits for the debounce, if any.
          if (next === "" || (config.debounceMs ?? 0) <= 0) config.onValueChange(next);
        }}
        placeholder={config.placeholder}
        label={config.label}
        clearLabel={mobileLabels.clearSearch}
      />
    );
  }

  if (!expanded && !persistentExpanded) {
    return (
      <Button
        type='button'
        variant='ghost'
        size='icon'
        aria-label={config.label}
        title={toolbarIconTitle(config.label)}
        onClick={() => setExpanded(true)}
      >
        <SearchIcon />
      </Button>
    );
  }

  return (
    <div ref={containerRef} className={cn("relative shrink-0", isMobile ? "w-full" : widthClassName)}>
      <SearchIcon className='pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground' />
      <Input
        ref={inputRef}
        value={draft}
        placeholder={config.placeholder}
        aria-label={config.label}
        className='h-10 pr-10 pl-9'
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          if ((config.debounceMs ?? 0) <= 0) config.onValueChange(next);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          closeSearch();
        }}
      />
      <button
        type='button'
        className='absolute top-1/2 right-1 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/45 focus-visible:outline-none'
        aria-label={draft ? `Clear ${config.label}` : `Close ${config.label}`}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={closeSearch}
      >
        <XIcon className='size-4' />
      </button>
    </div>
  );
}

function ToolbarSorting<TData>({
  config,
  table,
}: {
  config: DataTableToolbarSortingConfig;
  table: Table<TData>;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const sorting = table.getState().sorting;

  if (config.render) {
    return (
      <>
        {config.render({
          isMobile,
          inOverflow: false,
          close: () => setOpen(false),
          sorting,
          onSortingChange: (next) => table.setSorting(next),
        })}
      </>
    );
  }

  const current = sorting[0];
  const trigger = (
    <Button type='button' variant='ghost' size='icon' aria-label={config.label} title={config.label}>
      <ArrowUpDownIcon />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align='end' className='w-64 p-1.5'>
        <div className='space-y-0.5'>
          {config.items.map((item) => {
            const active = current?.id === item.id;
            return (
              <button
                key={item.id}
                type='button'
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-sm hover:bg-accent",
                  active && "bg-accent font-medium",
                )}
                onClick={() => table.setSorting([{ id: item.id, desc: active ? Boolean(current.desc) : false }])}
              >
                {item.label}
                {active ? current.desc ? <ArrowDownIcon className='size-4' /> : <ArrowUpIcon className='size-4' /> : null}
              </button>
            );
          })}
        </div>
        <div className='mt-1 grid grid-cols-2 gap-1 border-t pt-1'>
          <Button
            type='button'
            variant={!current?.desc ? "secondary" : "ghost"}
            size='sm'
            disabled={!current}
            onClick={() => current && table.setSorting([{ id: current.id, desc: false }])}
          >
            <ArrowUpIcon />
            {config.ascendingLabel}
          </Button>
          <Button
            type='button'
            variant={current?.desc ? "secondary" : "ghost"}
            size='sm'
            disabled={!current}
            onClick={() => current && table.setSorting([{ id: current.id, desc: true }])}
          >
            <ArrowDownIcon />
            {config.descendingLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ToolbarViews<TValue extends string>({
  config,
}: {
  config: DataTableToolbarViewsConfig<TValue>;
}) {
  const isMobile = useIsMobile();
  if (config.render) {
    return (
      <>
        {config.render({
          isMobile,
          inOverflow: false,
          close: () => undefined,
          value: config.value,
          items: config.items,
        })}
      </>
    );
  }

  return (
    <SegmentedControl
      value={config.value}
      onValueChange={(value) => {
        const selected = config.items.find((item) => item.value === value);
        if (selected) config.onValueChange(selected.value);
      }}
      items={config.items}
      variant='table-view'
      size='sm'
      itemClassName='whitespace-nowrap px-3'
    />
  );
}

function ToolbarContent<TData, TValue extends string>({
  table,
  config,
  filter,
}: {
  table: Table<TData>;
  config: DataTableToolbarConfig<TData, TValue>;
  filter?: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const selectedCount = table.getFilteredSelectedRowModel().rows.length;
  const bulkActive = Boolean(config.bulkActions && selectedCount > 0);
  const actions = bulkActive ? config.bulkActions?.actions ?? [] : config.secondaryActions ?? [];
  const primaryAction = bulkActive ? undefined : config.primaryAction;
  const hasLeftContent = Boolean(config.views?.items.length);
  const desktopSearch = !bulkActive && !isMobile && config.search ? <ToolbarSearch config={config.search} /> : null;
  const desktopLeftSearch =
    !bulkActive && !isMobile && !hasLeftContent && config.search ? (
      <ToolbarSearch config={config.search} persistentExpanded />
    ) : null;

  const display = config.display?.render ? (
    config.display.render({
      isMobile,
      inOverflow: false,
      close: () => undefined,
    })
  ) : config.display ? (
    <DataTableViewOptions
      table={table}
      iconOnly
      responsiveSheet
      showReset
      labels={{
        trigger: config.display.label,
        active: config.display.activeColumnsLabel,
        available: config.display.availableColumnsLabel,
        empty: config.display.emptyColumnsLabel,
        reset: config.display.resetLabel,
      }}
    />
  ) : null;

  const controls = (
    <div className={isMobile ? "contents" : "flex min-w-0 items-center justify-end gap-1"}>
      {bulkActive && config.bulkActions ? (
        <span className='mr-2 whitespace-nowrap text-sm text-muted-foreground'>
          {config.bulkActions.selectedLabel(selectedCount)}
        </span>
      ) : null}
      {!bulkActive && config.sorting ? <ToolbarSorting config={config.sorting} table={table} /> : null}
      {!bulkActive ? filter : null}
      {hasLeftContent ? desktopSearch : null}
      {!bulkActive ? display : null}
      <DataTableToolbarActions primaryAction={primaryAction} secondaryActions={actions} />
    </div>
  );

  if (isMobile) {
    return <MobileToolbarContent table={table} config={config} filter={filter} />;
  }

  if (config.inlineSearch && hasLeftContent && config.views) {
    return (
      <div className='flex min-w-0 flex-wrap items-center gap-3'>
        <div className='shrink-0'>
          <ToolbarViews config={config.views} />
        </div>
        {!bulkActive && config.search ? (
          <ToolbarSearch config={config.search} persistentExpanded widthClassName='w-[260px]' />
        ) : null}
        {!bulkActive ? filter : null}
        <div className='ml-auto flex min-w-0 items-center justify-end gap-1'>
          {bulkActive && config.bulkActions ? (
            <span className='mr-2 whitespace-nowrap text-sm text-muted-foreground'>
              {config.bulkActions.selectedLabel(selectedCount)}
            </span>
          ) : null}
          {!bulkActive && config.sorting ? <ToolbarSorting config={config.sorting} table={table} /> : null}
          {!bulkActive ? display : null}
          <DataTableToolbarActions primaryAction={primaryAction} secondaryActions={actions} />
        </div>
      </div>
    );
  }

  if (!hasLeftContent) {
    return (
      <div className='flex min-w-0 items-center gap-3'>
        {desktopLeftSearch ? <div className='min-w-0 flex-1'>{desktopLeftSearch}</div> : null}
        <div className='ml-auto'>{controls}</div>
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 items-center", "gap-3")}>
      {config.views ? <ToolbarViews config={config.views} /> : null}
      <div className='ml-auto'>{controls}</div>
    </div>
  );
}

/** The phone's view chips (with counts). */
function MobileViewChips<TValue extends string>({ config }: { config: DataTableToolbarViewsConfig<TValue> }) {
  // One row that scrolls sideways when the labels are long, edge to edge, rather than wrapping onto two lines.
  return (
    <div className='scrollbar-none -mx-4 -my-1 flex min-w-0 items-center gap-1.5 overflow-x-auto px-4 py-1'>
      {config.items.map((item) => {
        const active = item.value === config.value;
        return (
          <button
            key={item.value}
            type='button'
            aria-pressed={active}
            disabled={item.disabled}
            onClick={() => config.onValueChange(item.value)}
            className={cn(
              "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13px] whitespace-nowrap transition-colors disabled:opacity-50",
              active
                ? "bg-primary/12 font-extrabold text-primary ring-1 ring-primary/25"
                : "bg-card font-bold text-muted-foreground ring-1 ring-border/60",
            )}
          >
            {item.label}
            {typeof item.count === "number" ? (
              <span className={cn("tabular-nums", active ? "text-primary/80" : "text-muted-foreground/80")}>{item.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Phone sort control: a round button beside the search (dot when a sort is set), opening a sheet of fields. */
function MobileSortButton<TData>({ config, table }: { config: DataTableToolbarSortingConfig; table: Table<TData> }) {
  const labels = useDataTableMobileLabels();
  const [open, setOpen] = React.useState(false);
  const current = table.getState().sorting[0];
  const currentLabel = config.items.find((item) => item.id === current?.id)?.label;
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          type='button'
          aria-label={currentLabel ? labels.sortBy(currentLabel) : labels.sort}
          title={currentLabel ? labels.sortBy(currentLabel) : labels.sort}
          className='relative inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-card text-foreground ring-1 ring-border/70'
        >
          <ArrowUpDownIcon aria-hidden className='size-[1.125rem]' />
          {currentLabel ? <span aria-hidden className='absolute top-2.5 right-2.5 size-2 rounded-full bg-primary' /> : null}
        </button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
          <DrawerTitle className='text-lg font-bold'>{config.label}</DrawerTitle>
          <DrawerDescription className='sr-only'>{config.label}</DrawerDescription>
        </DrawerHeader>
        <div className='flex flex-col gap-3 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]'>
          <ul className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/60'>
            {config.items.map((item) => {
              const active = current?.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    type='button'
                    aria-current={active ? "true" : undefined}
                    onClick={() => table.setSorting([{ id: item.id, desc: active ? Boolean(current?.desc) : false }])}
                    className={cn(
                      "flex min-h-12 w-full items-center justify-between px-4 text-left text-[15px] active:bg-muted",
                      active ? "font-bold text-primary" : "font-medium text-foreground",
                    )}
                  >
                    {item.label}
                    {active ? <CheckIcon aria-hidden className='size-4' /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className='grid grid-cols-2 gap-2'>
            <Button
              type='button'
              variant={!current?.desc ? "secondary" : "outline"}
              disabled={!current}
              className='h-11 rounded-xl'
              onClick={() => current && table.setSorting([{ id: current.id, desc: false }])}
            >
              <ArrowUpIcon />
              {config.ascendingLabel}
            </Button>
            <Button
              type='button'
              variant={current?.desc ? "secondary" : "outline"}
              disabled={!current}
              className='h-11 rounded-xl'
              onClick={() => current && table.setSorting([{ id: current.id, desc: true }])}
            >
              <ArrowDownIcon />
              {config.descendingLabel}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

type SheetAction = Pick<DataTableAction, "id" | "label" | "icon" | "onSelect" | "disabled" | "variant" | "children">;

/** A bottom sheet of actions (sub-actions listed under their parent), used by the selection bar. */
function MobileActionSheet({
  open,
  onOpenChange,
  title,
  actions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  actions: SheetAction[];
}) {
  const run = (action: Pick<DataTableAction, "onSelect">) => {
    onOpenChange(false);
    action.onSelect?.();
  };
  const rowClassName = (variant?: DataTableAction["variant"]) =>
    cn(
      "flex min-h-12 w-full items-center gap-3 px-4 text-left text-[15px] font-semibold active:bg-muted disabled:opacity-50",
      variant === "destructive" ? "text-destructive" : "text-foreground",
    );
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
          <DrawerTitle className='text-lg font-bold'>{title}</DrawerTitle>
          <DrawerDescription className='sr-only'>{title}</DrawerDescription>
        </DrawerHeader>
        <div className='flex flex-col gap-3 overflow-y-auto [&>*]:shrink-0 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]'>
          {actions.map((action) =>
            action.children?.length ? (
              <section key={action.id} className='flex flex-col gap-1.5'>
                <h3 className='px-1 text-xs font-bold tracking-wide text-muted-foreground uppercase'>{action.label}</h3>
                <ul className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/60'>
                  {action.children.map((child) => (
                    <li key={child.id}>
                      <button type='button' disabled={child.disabled} onClick={() => run(child)} className={rowClassName(child.variant)}>
                        {child.icon}
                        {child.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : (
              <ul key={action.id} className='overflow-hidden rounded-2xl bg-muted/60'>
                <li>
                  <button type='button' disabled={action.disabled} onClick={() => run(action)} className={rowClassName(action.variant)}>
                    {action.icon}
                    {action.label}
                  </button>
                </li>
              </ul>
            ),
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Selection mode on a phone: a header (cancel, count, select all) in place of the toolbar, and the bulk actions
 * in a bar pinned above the bottom navigation. The first action is the button; the rest share the ⋯ sheet.
 */
function MobileSelectionBar<TData>({
  table,
  bulk,
  onCancel,
}: {
  table: Table<TData>;
  bulk?: DataTableToolbarBulkActionsConfig;
  onCancel: () => void;
}) {
  const labels = useDataTableMobileLabels();
  const selectedCount = table.getFilteredSelectedRowModel().rows.length;
  const [primaryOpen, setPrimaryOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [first, ...rest] = bulk?.actions ?? [];

  return (
    <>
      <div className='flex h-11 min-w-0 items-center gap-2.5'>
        <Button
          type='button'
          variant='outline'
          size='icon'
          aria-label={labels.cancelSelection}
          onClick={onCancel}
          className='size-10 shrink-0 rounded-full'
        >
          <XIcon />
        </Button>
        <span className='min-w-0 flex-1 truncate text-[19px] font-extrabold text-foreground'>
          {labels.selectedCount(selectedCount)}
        </span>
        <button
          type='button'
          onClick={() => table.toggleAllRowsSelected(true)}
          className='shrink-0 px-1 py-2 text-[15px] font-extrabold text-primary'
        >
          {labels.selectAll}
        </button>
      </div>
      {first && typeof document !== "undefined" ? createPortal(
        // Covers the bottom navigation while selecting, on the same canvas strip, so the actions sit where the
        // thumb already is and a stray tab tap cannot drop the selection. Portalled to <body>: an ancestor with
        // a transform (pull-to-refresh) would otherwise pin this "fixed" bar to the end of a long list.
        <div
          // Same strip as the bottom navigation (its height, gutters and top fade), but solid wherever the tab pill
          // sits, so nothing of the navigation shows through while selecting.
          className='fixed inset-x-0 bottom-0 z-50 flex items-end gap-2.5'
          style={{
            minHeight: "var(--app-shell-mobile-bottom-nav-offset, calc(6.5rem + env(safe-area-inset-bottom)))",
            // Clear of the home indicator and the screen's rounded corners (e.g. iPhone 15), even in a browser
            // that reports no safe area.
            paddingBottom: "max(1.25rem, calc(env(safe-area-inset-bottom) + 0.75rem))",
            paddingLeft: "max(1rem, calc(env(safe-area-inset-left) + 0.5rem))",
            paddingRight: "max(1rem, calc(env(safe-area-inset-right) + 0.5rem))",
            background:
              "linear-gradient(to top, var(--app-canvas, var(--background)) calc(100% - 1.75rem), transparent)",
          }}
        >
          {/* The header scrolls away on a long list; leaving selection stays one tap away down here. */}
          <Button
            type='button'
            variant='outline'
            aria-label={labels.cancelSelection}
            onClick={onCancel}
            className='size-16 shrink-0 rounded-full bg-card shadow-[0_8px_24px_rgba(16,24,40,0.12)]'
          >
            <XIcon />
          </Button>
          <Button
            type='button'
            disabled={selectedCount === 0 || first.disabled}
            onClick={() => (first.children?.length ? setPrimaryOpen(true) : first.onSelect?.())}
            className='h-16 flex-1 rounded-full text-[15px] font-extrabold shadow-[0_8px_20px_rgba(5,150,105,0.3)]'
          >
            {first.icon}
            {first.label}
            {selectedCount > 0 ? <span className='rounded-full bg-white/20 px-2 py-0.5 text-[13px] tabular-nums'>{selectedCount}</span> : null}
          </Button>
          {rest.length > 0 ? (
            <Button
              type='button'
              variant='outline'
              disabled={selectedCount === 0}
              aria-label={labels.moreActions}
              onClick={() => setMoreOpen(true)}
              className='size-16 shrink-0 rounded-full bg-card shadow-[0_8px_24px_rgba(16,24,40,0.12)]'
            >
              <MoreHorizontalIcon />
            </Button>
          ) : null}
        </div>,
        document.body,
      ) : null}
      {first?.children?.length ? (
        <MobileActionSheet open={primaryOpen} onOpenChange={setPrimaryOpen} title={first.label} actions={first.children} />
      ) : null}
      {rest.length > 0 ? (
        <MobileActionSheet open={moreOpen} onOpenChange={setMoreOpen} title={labels.moreActions} actions={rest} />
      ) : null}
    </>
  );
}

/**
 * Round buttons the phone title row holds beside the title: the screen's one primary action and a ⋯ for the rest.
 * Two, not more, so a title beside a back button still has room to be read whole (see MobileScreenHeader).
 */
const MOBILE_HEADER_MAX_BUTTONS = 2;

/**
 * An action that can sit in the title row as an icon: a plain action with an icon, or a self-drawn one that says it
 * draws an icon on phones (export). A self-drawn filter or dialog does not, and folds into ⋯.
 */
function isMobileHeaderInlineable(action: DataTableAction): boolean {
  if (action.hideOnMobile || action.children?.length) return false;
  return action.render ? Boolean(action.mobileIconOnly) : Boolean(action.icon);
}

/**
 * Which secondary actions stay in the phone title row as round icon buttons, and which fold into ⋯.
 *
 * All of them inline when they fit beside the primary action(s) and each has an icon, so a list with one primary
 * and a refresh shows the refresh icon instead of a ⋯ hiding it. Otherwise ⋯ takes one place and the first icon
 * actions fill what is left. Inline ones are marked `priority` (the toolbar's inline icon cluster) with the
 * outline look of the ⋯ button.
 */
export function splitMobileHeaderActions(
  actions: DataTableAction[],
  primaryCount: number,
): { inline: DataTableAction[]; folded: DataTableAction[] } {
  const room = Math.max(0, MOBILE_HEADER_MAX_BUTTONS - primaryCount);
  const asInline = (action: DataTableAction): DataTableAction => ({ ...action, emphasis: "priority", variant: action.variant ?? "outline" });
  if (actions.length <= room && actions.every(isMobileHeaderInlineable)) {
    return { inline: actions.map(asInline), folded: [] };
  }
  const inlineIds = new Set(
    actions
      .filter(isMobileHeaderInlineable)
      .slice(0, Math.max(0, room - 1))
      .map((action) => action.id),
  );
  return {
    inline: actions.filter((action) => inlineIds.has(action.id)).map(asInline),
    folded: actions.filter((action) => !inlineIds.has(action.id)),
  };
}

/**
 * The toolbar on a phone (the mobile list design): the page's title row with its actions (primary button and ⋯,
 * where "Select" joins the secondary actions), a full-width search, then the view chips with filter and sort.
 * In selection mode the whole toolbar becomes the selection header.
 */
function MobileToolbarContent<TData, TValue extends string>({
  table,
  config,
  filter,
}: {
  table: Table<TData>;
  config: DataTableToolbarConfig<TData, TValue>;
  filter?: React.ReactNode;
}) {
  const labels = useDataTableMobileLabels();
  const list = useDataTableMobileList();
  const selecting = Boolean(list?.selecting);

  if (selecting && list) {
    return <MobileSelectionBar table={table} bulk={config.bulkActions} onCancel={() => list.setSelecting(false)} />;
  }

  // Secondary actions (refresh, export, select…) join the title row as round icon buttons while they fit (see
  // `splitMobileHeaderActions`); only what does not fit folds into ⋯.
  // Only the FIRST primary action keeps its own button on a phone; any others (Transactions has income AND
  // expense) open from ⋯ with their labels, so the title row never holds more than two buttons.
  const primaryList = config.primaryAction ? (Array.isArray(config.primaryAction) ? config.primaryAction : [config.primaryAction]) : [];
  const [mobilePrimary, ...extraPrimaries] = primaryList.filter((action) => !action.hideOnMobile);
  const allSecondaryActions: DataTableAction[] = [
    ...extraPrimaries.map((action) => ({ ...action, emphasis: "secondary" as const })),
    ...(config.secondaryActions ?? []).map((action) => ({ ...action, emphasis: "secondary" as const })),
    ...(config.bulkActions && list
      ? [
          {
            id: "__mobile-select",
            label: labels.select,
            description: labels.selectDescription,
            icon: <ListChecksIcon className='size-4' />,
            emphasis: "secondary" as const,
            order: 10_000,
            onSelect: () => list.setSelecting(true),
          },
        ]
      : []),
  ];
  const primaryCount = mobilePrimary ? 1 : 0;
  const { inline: inlineActions, folded: secondaryActions } = splitMobileHeaderActions(allSecondaryActions, primaryCount);
  const hasActions = Boolean(mobilePrimary) || allSecondaryActions.length > 0;
  const hasTools = Boolean(filter) || Boolean(config.sorting && !config.sorting.render);
  // A tab's list has no title row (the app header above is its title). Its actions then join the search row
  // instead of standing alone on an empty row: one round button when a single action fits, otherwise all of
  // them, the primary included, in ⋯ (creating also lives in the app's own + button).
  const actionsInSearchRow = !config.mobileHeader && hasActions && Boolean(config.search || hasTools);
  const searchRowActions = actionsInSearchRow
    ? splitMobileHeaderActions(
        [...(mobilePrimary ? [{ ...mobilePrimary, emphasis: "secondary" as const }] : []), ...allSecondaryActions],
        MOBILE_HEADER_MAX_BUTTONS - 1,
      )
    : null;

  return (
    <div className='flex min-w-0 flex-col gap-3'>
      {config.mobileHeader || (hasActions && !actionsInSearchRow) ? (
        <DataTableMobileHeaderRow header={config.mobileHeader}>
          <DataTableToolbarActions
            primaryAction={mobilePrimary}
            secondaryActions={[...inlineActions, ...secondaryActions]}
          />
        </DataTableMobileHeaderRow>
      ) : null}
      {config.mobileTabs}
      {config.search || hasTools ? (
        <div className='flex min-w-0 items-center gap-2'>
          <div className='min-w-0 flex-1'>{config.search ? <ToolbarSearch config={config.search} persistentExpanded /> : null}</div>
          {filter ? (
            <div className='shrink-0 [&_button]:size-11 [&_button]:rounded-full [&_button]:bg-card [&_button]:ring-1 [&_button]:ring-border/70'>
              {filter}
            </div>
          ) : null}
          {config.sorting && !config.sorting.render ? <MobileSortButton config={config.sorting} table={table} /> : null}
          {searchRowActions ? (
            <div className='shrink-0'>
              <DataTableToolbarActions secondaryActions={[...searchRowActions.inline, ...searchRowActions.folded]} />
            </div>
          ) : null}
        </div>
      ) : null}
      {config.mobileIntro}
      {config.views?.items.length ? <MobileViewChips config={config.views} /> : null}
    </div>
  );
}


/**
 * The phone title row: the shared MobileScreenHeader (back, title, up to two round actions). Exported so a phone
 * screen that is not a table (a grid, a summary) draws the same header as the lists. Not pinned itself: the
 * DataTable pins its whole toolbar, and `MobileListChromeHeader` pins its own wrapper.
 */
export function DataTableMobileHeaderRow({
  header,
  children,
}: {
  header?: DataTableToolbarConfig<unknown>["mobileHeader"];
  children?: React.ReactNode;
}) {
  if (!header) {
    // Actions with no title (a list embedded in a larger screen): just the buttons, at the end of the row.
    return (
      <div className='flex min-w-0 items-center justify-end gap-2 [&_button]:!rounded-full [&>*>button]:size-11'>{children}</div>
    );
  }
  return (
    <MobileScreenHeader
      title={header.title}
      subtitle={header.subtitle}
      back={header.back}
      actions={children}
      sticky={false}
    />
  );
}

export function DataTableConfiguredToolbar<TData, TValue extends string>({
  table,
  config,
}: {
  table: Table<TData>;
  config: DataTableToolbarConfig<TData, TValue>;
}) {
  if (!config.filters) {
    return (
      <div className={config.className}>
        <ToolbarContent table={table} config={config} />
      </div>
    );
  }

  return (
    <DataTableFilters
      config={{ ...config.filters.config, search: undefined }}
      appliedValues={config.filters.appliedValues}
      onApply={config.filters.onApply}
      onReset={config.filters.onReset}
      compactTrigger
      className={config.className}
      renderToolbar={({ filter }) => <ToolbarContent table={table} config={config} filter={filter} />}
    />
  );
}
